import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useEffect } from 'react';

import ExclamationYellow from '../../assets/svg/ExclamationYellow';
import useTimeout from '../../hooks/useTimeout';
import useTranslation from '../../hooks/useTranslation';
import { useUserSession } from '../../hooks/useUserSession';
import { DialogActionStyles, DialogButton, DialogContentStyles, smallScreenSX } from './SessionDialog.styles';

interface SessionDialogProps {
    /**
     * Number of seconds without any events to consider the user to be idle
     */
    maxIdleTime?: number;

    /**
     * Number of seconds to display a dialog for extending the session
     */
    logoutWarnTime?: number;

    /**
     * Is called when the user has been inactive for the max idle time and the logout warn time
     */
    onLogout: () => void;
}

/**
 * A component that will monitor the user activity and show a popup if the
 * user has been idle for a max amount of time.
 *
 * If the session time has passed it will popup a warning that will automatically
 * log out the user within a certain time or give the user the possibility to extend
 * the session;
 */
const SessionDialog = ({ maxIdleTime, logoutWarnTime, onLogout }: SessionDialogProps) => {
    const translate = useTranslation();
    const { isSessionActive, resetSession } = useUserSession(maxIdleTime);
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const onKeepLoggedIn = () => {
        resetSession();
    };

    const {
        stopAndReset: logoutTimeStopAndReset,
        start: logoutTimeStart,
        isStarted: isLogoutTimeStarted,
    } = useTimeout(logoutWarnTime, true, onLogout);

    useEffect(() => {
        logoutTimeStopAndReset();

        if (!isSessionActive) {
            logoutTimeStart();
        }
    }, [isSessionActive]);

    return (
        <>
            {isLogoutTimeStarted && (
                <Dialog
                    open={!isSessionActive}
                    onClose={onKeepLoggedIn}
                    data-testid="session-dialog"
                    maxWidth={'sm'}
                    fullWidth
                    sx={isSmallScreen ? smallScreenSX : undefined}
                    scroll="paper">
                    <DialogContent sx={DialogContentStyles}>
                        <ExclamationYellow />
                        <DialogTitle>{translate('session.expire.title')}</DialogTitle>
                        <DialogContentText>{translate('session.expire.description')}</DialogContentText>
                    </DialogContent>
                    <DialogActions sx={DialogActionStyles}>
                        <Button onClick={onLogout} variant="text" sx={DialogButton}>
                            {translate('session.logout')}
                        </Button>
                        <Button
                            onClick={onKeepLoggedIn}
                            variant="outlined"
                            data-testid="session-dialog-extend"
                            sx={DialogButton}>
                            {translate('session.extend')}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
};

export default SessionDialog;
